import * as t from "io-ts";
import { omit } from "lodash";
import { CompanyIO } from "../company-io";
import { ConfigIO } from "../config-io";

export const TodoNoteTagIO = t.partial({
  key: t.string,
  value: t.string,
})
export const UrlReferenceIO = t.partial({
	url: t.string,
	assets_id: t.string,
	name: t.string,
	file_name: t.string,
	description: t.string,
	type: t.string,
	fileUrl:t.string,
})
export const AssetPropIO = t.partial({
	asset_id: t.string,
	resource_type: t.string,
	resource_id: t.string,
	creation_user: t.string,
	creation_time: t.number,
	content_type: t.string,
	filename: t.string,
	name:t.string,
	filesize: t.number,
	app_id: t.string,
	asset_type: t.string
})
export const UrlExternalReferenceIO = t.partial({
	reference_type: t.literal('URL'),
	url_reference: UrlReferenceIO,
	asset_properties:AssetPropIO,
})

export const AssetReferenceIO = t.partial({
	id: t.string,
	name: t.string,
	environment: t.string,
	description: t.string,
	timestamp: t.number,
	user: t.string,
	fileUrl:t.string,
})
export type AssetReferenceT = t.TypeOf<typeof AssetReferenceIO>

export const AttachmentExternalReferenceIO = t.type({
	reference_type: t.literal('ATTACHMENT'),
	asset_reference: AssetReferenceIO,
})
export type AttachmentExternalReferenceT = t.TypeOf<
	typeof AttachmentExternalReferenceIO
>
export type UrlExternalReferenceT = t.TypeOf<
	typeof UrlExternalReferenceIO
>

const ReferenceIO = t.union([
	UrlExternalReferenceIO,
	AttachmentExternalReferenceIO,
])
export type ReferenceT = t.TypeOf<typeof ReferenceIO>
export type TodoNoteTagT = t.TypeOf<typeof TodoNoteTagIO>

export const TodoNoteIO = t.intersection([
  t.type({
    companyID: t.string,
    taskId: t.string,
    timestamp: t.number,
    text: t.string,
    user: t.string,
  }),
  t.partial({
    noteId: t.string,
    tags: t.array(TodoNoteTagIO),
    source: t.string,
    isAttachment: t.boolean,
    isPinned: t.boolean,
    references: t.array(ReferenceIO),
  }),
]);
export type TodoNoteT = t.TypeOf<typeof TodoNoteIO>;

export type NoteToAddT = Omit<TodoNoteT, "user" | "timestamp">;

// const UrlReferenceIO = t.partial({
//   url: t.string,
//   name: t.string,
//   description: t.string,
//   type: t.string,
// });

// const UrlExternalReferenceIO = t.partial({
//   reference_type: t.literal("URL"),
//   url_reference: UrlReferenceIO,
// });

// const AssetReferenceIO = t.partial({
//   id: t.string,
//   name: t.string,
//   environment: t.string,
//   description: t.string,
//   timestamp: t.number,
//   user: t.string,
// });
// export type AssetReferenceT = t.TypeOf<typeof AssetReferenceIO>;

// export const AttachmentExternalReferenceIO = t.type({
//   reference_type: t.literal("ATTACHMENT"),
//   asset_reference: AssetReferenceIO,
// });
// export type AttachmentExternalReferenceT = t.TypeOf<
//   typeof AttachmentExternalReferenceIO
// >;

// const ReferenceIO = t.union([
//   UrlExternalReferenceIO,
//   AttachmentExternalReferenceIO,
// ]);
// export type ReferenceT = t.TypeOf<typeof ReferenceIO>;

export const TaskInfoIO = t.partial({
  title: t.string,
  description: t.string,
  details: t.string,
  project: t.string,
});

export type TaskInfoT = t.TypeOf<typeof TaskInfoIO>;

export const taskTypes = {
  /* New Brand Task */
  WEB_SCRAPE_CONFIG: "WEB_SCRAPE_CONFIG",
  /* Manual detail Task */
  MANUAL: "MANUAL",
} as const;
export const taskTypeList = Object.values(taskTypes);

export const taskTypeToNull = {
  // will be expanded in the future to support maintenance
  WEB_SCRAPE_CONFIG: null,
  MANUAL: null,
} as const;
export const TaskTypeIO = t.keyof(taskTypeToNull);
export type TaskTypeT = t.TypeOf<typeof TaskTypeIO>;

export const RawTaskIO = t.intersection([
  t.type({
    companyID: t.string,
    task_id: t.string,
    companyDetail: CompanyIO,
    configuration: ConfigIO,
    isFastlaneTask: t.boolean,
  }),
  t.partial({
    oldCompanyDetail: CompanyIO,
    backlog_id: t.string,
    backlog_item_id: t.string,
    info: TaskInfoIO,
    priority: t.number,
    assignee: t.union([t.null, t.string]),
    created_at: t.number,
    last_modified_at: t.union([t.number, t.null]),
    last_modified_by: t.union([t.string, t.null]),
    last_test_run: t.union([
      t.partial({ run_id: t.string, status: t.string }),
      t.null,
    ]),
    notes: t.array(TodoNoteIO),
    scheduled_at: t.union([t.null, t.number]),
    current_state: t.string,
    state: t.array(t.UnknownRecord),
    tags: t.array(t.string),
    type: TaskTypeIO,
    title: t.string,
  }),
]);

export type RawTaskT = t.TypeOf<typeof RawTaskIO>;

export type AssignmentTypeT =
  | "Pending"
  | "Inbox"
  | "InProgress"
  | "InReview"
  | "Validation"
  | "Approved"
  | "Rejected"
  | "Completed";

export const SortOrderIO = t.union([t.literal("ASC"), t.literal("DSC")]);
export type SortOrderT = t.TypeOf<typeof SortOrderIO>;

/**
 * Please keep the order of the states in the same flow that they are meant to
 * be handled
 */
export const fsmStateToNull = {
  // _UNASSIGNED_ is a fictive state that backend exposes for todos without an FSM
  _UNASSIGNED_: "_Unassigned_",
  CREATED: "Created",
  IN_EDITING: "In Editing",
  WAITING_FOR_EDITING: "Waiting for Editing",
  READY_FOR_PUBLICATION: "Ready for Publication",
  IN_PUBLISH: "In Publish",
  CLOSED: "Closed",
  ESCALATED: "Escalated",
  ESCALATE_REASONS: "Escalate Reasons",
  BLOCK_REASONS: "Block Reasons",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  REJECTED_BY_PUBLISHER_TO_EDITOR:"Rejected by Publisher",
  COMPLETED: "Completed",
  BLOCKED: "Blocked",
  DETACHED: "Detached",
  PAUSED: "Paused",
  ARCHIVED: "Archived",
};
export const FsmStateIO = t.keyof(fsmStateToNull);
export type FsmStateT = t.TypeOf<typeof FsmStateIO>;

/** _UNASSIGNED_ is not a valid state so we want to exclude/omit it */
export const TaskStatesIO = t.keyof(omit(fsmStateToNull, "_UNASSIGNED_"));
export type TaskStateT = t.TypeOf<typeof TaskStatesIO>;

const taskAssignmentToNull = {
  CONFIG: null,
  PICK_EDITING: null,
  VALIDATOR: null,
};
export const TaskAssignmentIO = t.keyof(taskAssignmentToNull);
export type TaskAssignmentT = t.TypeOf<typeof TaskAssignmentIO>;
const taskAssignmentsIO = t.array(TaskAssignmentIO);
export const TaskAssignmentPayloadIO = t.intersection([
  t.type({
    assignments: taskAssignmentsIO,
  }),
  t.partial({
    fsm_group: t.intersection([
      t.type({ fsm_group_id: t.string }),
      t.partial({ fsm_group_revision: t.number }),
    ]),
    workstreamIds: t.array(t.string),
    todo_filters: t.array(t.string),
  }),
]);

export type TaskAssignmentPayloadT = t.TypeOf<typeof TaskAssignmentPayloadIO>;

const taskAdminActionToNull = {
  PENDING: null,
  REASSIGN: null,
  UNASSIGN: null,
  REJECTED_BY_PUBLISHER: null,
};
export const TaskAdminActionIO = t.keyof(taskAdminActionToNull);
export const taskAdminActionKeys = Object.keys(
  taskAdminActionToNull
) as (keyof typeof taskAdminActionToNull)[];
export type TaskAdminActionT = t.TypeOf<typeof TaskAdminActionIO>;
const taskBulkActionToNull = {
  UNBLOCK: null,
  BLOCK: null,
};
export const TaskBulkActionIO = t.keyof(taskBulkActionToNull);
export type TaskBulkActionT = t.TypeOf<typeof TaskBulkActionIO>;

const taskPublishingActionToNull = {
  START_PUBLICATION: null,
};
export const TaskPublishingActionIO = t.keyof(taskPublishingActionToNull);
export const taskPublishingActionKeys = Object.keys(
  taskPublishingActionToNull
) as (keyof typeof taskPublishingActionToNull)[];
export type TaskPublishingActionT = t.TypeOf<typeof TaskPublishingActionIO>;

export type BulkEventType = TaskAdminActionT | TaskPublishingActionT;

export type BulkActionType = TaskBulkActionT;

export const RejectReasonIO = t.intersection([
  t.type({ reason: t.string }),
  t.partial({ field: t.string, details: t.string }),
]);

export type RejectReason = t.TypeOf<typeof RejectReasonIO>;
